import {
  KeyboardEvent,
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CanvasContainer,
  Container,
  DEFAULT_HEIGHT,
  LARGE_HEIGHT,
  type Props,
  SEEK_STEP,
  TimeDisplay,
  formatTime,
} from "./Waveform.Parts";
import { Overlay } from "./overlay/Overlay";
import { useWaveform } from "./use-waveform/UseWaveform";

export const Waveform = ({
  waveform,
  totalDuration,
  currentTime,
  handlePausePlay,
  large = false,
  showCurrentTime,
  showDuration,
  disabled = false,
  onSeek,
  onMove,
  onDrag,
  overlay,
}: Props) => {
  const height = large ? LARGE_HEIGHT : DEFAULT_HEIGHT;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouch, setLastTouch] = useState<Touch | null>(null);
  const lastTouchEndTimeRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        setCanvasWidth(entries[0].contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useWaveform({
    canvasRef,
    waveform,
    width: canvasWidth,
    height,
    currentTime,
    totalDuration,
    isHovering,
  });

  const getDurationFromEvent = useCallback(
    (
      event:
        | MouseEvent
        | TouchEvent
        | globalThis.MouseEvent
        | globalThis.TouchEvent,
    ): number => {
      const canvas = canvasRef.current;
      if (!canvas || canvasWidth === 0) return 0;

      let clientX = 0;
      if ("touches" in event) {
        const touch =
          event.touches[0] ||
          (event as globalThis.TouchEvent).changedTouches?.[0] ||
          lastTouch;
        clientX = touch?.clientX ?? 0;
      } else {
        clientX = (event as MouseEvent).clientX;
      }

      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const duration = (x / canvasWidth) * totalDuration;
      return Math.max(0, Math.min(duration, totalDuration));
    },
    [canvasWidth, totalDuration, lastTouch],
  );

  //#region Touch Event Handlers
  const handleTouchStart = useCallback(
    (event: globalThis.TouchEvent) => {
      if (event.cancelable) event.preventDefault();

      onDrag?.(true);
      setIsDragging(true);
      if (event.touches.length > 0) {
        setLastTouch(event.touches[0]);
      }
      onSeek(getDurationFromEvent(event));
    },
    [getDurationFromEvent, onSeek, onDrag],
  );

  const handleMouseDown = (event: MouseEvent<HTMLCanvasElement>) => {
    // Safety check: If a touch just happened, ignore mouse down
    if (Date.now() - lastTouchEndTimeRef.current < 500) return;

    if (event.button !== 0) return;
    event.preventDefault();
    onDrag?.(true);
    setIsDragging(true);
    onSeek(getDurationFromEvent(event));
  };

  const handleMouseMove = useCallback(
    (event: globalThis.MouseEvent) => {
      const duration = getDurationFromEvent(event);
      if (isDragging) {
        onSeek(duration);
      } else if (onMove) {
        onMove(duration);
      }
    },
    [isDragging, onSeek, onMove, getDurationFromEvent],
  );

  const handleTouchMove = useCallback(
    (event: globalThis.TouchEvent) => {
      if (isDragging) {
        if (event.touches.length > 0) {
          setLastTouch(event.touches[0]);
        }
        onSeek(getDurationFromEvent(event));
      }
    },
    [isDragging, onSeek, getDurationFromEvent],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) onDrag?.(false);
    setIsDragging(false);
  }, [onDrag, isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (isDragging) onDrag?.(false);
    setIsDragging(false);
    lastTouchEndTimeRef.current = Date.now();
  }, [onDrag, isDragging]);
  //#endregion

  //#region Hover State Management
  const handleMouseEnter = () => {
    if (Date.now() - lastTouchEndTimeRef.current < 500) return;
    setIsHovering(true);
  };
  const handleMouseLeave = () => setIsHovering(false);
  //#endregion

  const handleKeyDown = (event: KeyboardEvent<HTMLCanvasElement>) => {
    switch (event.key) {
      case "ArrowLeft":
        onSeek(Math.max(0, currentTime - SEEK_STEP));
        break;
      case "ArrowRight":
        onSeek(Math.min(totalDuration, currentTime + SEEK_STEP));
        break;
      case "Home":
        onSeek(0);
        break;
      case "End":
        onSeek(totalDuration);
        break;
      case "Space":
      case " ":
        event.preventDefault();
        event.stopPropagation();
        handlePausePlay();
        break;
      default:
        break;
    }
  };

  //#region Event Listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mouseMoveListener = (e: globalThis.MouseEvent) => handleMouseMove(e);
    const touchMoveListener = (e: globalThis.TouchEvent) => handleTouchMove(e);
    const touchStartListener = (e: globalThis.TouchEvent) =>
      handleTouchStart(e);
    const mouseUpListener = () => handleMouseUp();
    const touchEndListener = () => handleTouchEnd();

    window.addEventListener("mousemove", mouseMoveListener);
    window.addEventListener("mouseup", mouseUpListener);
    window.addEventListener("touchend", touchEndListener);

    // Bind to Canvas to prevent default browser behavior causing ghost flicker
    window.addEventListener("touchmove", touchMoveListener, { passive: false });
    canvas.addEventListener("touchstart", touchStartListener, {
      passive: false,
    });

    return () => {
      window.removeEventListener("mousemove", mouseMoveListener);
      window.removeEventListener("touchmove", touchMoveListener);
      window.removeEventListener("mouseup", mouseUpListener);
      window.removeEventListener("touchend", touchEndListener);
      canvas.removeEventListener("touchstart", touchStartListener);
    };
  }, [
    handleMouseMove,
    handleTouchMove,
    handleMouseUp,
    handleTouchEnd,
    handleTouchStart,
  ]);

  return (
    <Container $disabled={disabled}>
      {showCurrentTime && (
        <TimeDisplay variant="small" disabled={disabled}>
          {formatTime(currentTime)}
        </TimeDisplay>
      )}
      <CanvasContainer
        ref={containerRef}
        $isDragging={isDragging}
        $disabled={disabled}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onKeyDown={handleKeyDown}
          role="slider"
          tabIndex={0}
          aria-label="Audio waveform"
          aria-valuemin={0}
          aria-valuemax={totalDuration}
          aria-valuenow={currentTime}
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(
            totalDuration,
          )}`}
        />
        {overlay && overlay.length > 1 && <Overlay overlay={overlay} />}
      </CanvasContainer>
      {showDuration && (
        <TimeDisplay variant="small" disabled={disabled}>
          {formatTime(totalDuration)}
        </TimeDisplay>
      )}
    </Container>
  );
};
